import type { RouteLocationNormalized } from 'vue-router';
import { defu } from 'defu';
import offerFlowContents from '~offerFlow/content/offerFlowContents';

/**
 * Used to load dynamic content for the offer flow of a product.
 * 1. Can fetch CMS offer flow content (from contentful)
 * 2. Can fetch listing details (from API listings endpoint or local listing details)
 *
 * Both sources provide content that is then attached to the product and used to display
 * content throughout the offer flow (e.g. headings, images etc.)
 */
export default function useOfferFlowContent(route?: RouteLocationNormalized) {
    const { locale } = useNuxtApp().$i18n;
    const { t: $t } = useNuxtApp().$i18n;

    /**
     * Fetch content from CMS based on offerFlowContentId defined in (dynamic) product config
     */
    function fetchCMSOfferFlowContent(id: string) {
        return $fetch<OfferFlowContent>('/api/cms/offerFlowContent', {
            query: {
                locale: locale.value,
                id,
            },
        });
    }

    /**
     * Load Offer Flow content from CMS, based on offerFlowContentId defined in (dynamic) product config.
     *
     * The content is only loaded if the locale of the content is different from the current (already loaded)
     * locale, or if there is no content yet. This prevents reloading the content on every page navigation.
     *
     * The content is then merged with existing default base content.
     *
     */
    async function applyCMSOfferFlowContent() {
        const { product } = useProduct(route);

        // If no offerFlowContentId is defined - we dont need to load any content
        // If there is one, but content is already loaded for the current locale, we also dont need to load it again
        if (!product.value.config.offerFlowContentId || (product.value.content?.cmsLocale === locale.value)) {
            return;
        }

        // Load and merge base content with CMS content
        try {
            const baseContent = offerFlowContents.find(content => content.key === product.value.config.key) ?? {} as OfferFlowContent;
            const content = await fetchCMSOfferFlowContent(product.value.config.offerFlowContentId);
            product.value.content = defu(content, baseContent);
        }
        catch (error) {
            throw createError({
                message: $t('offerFlow.productConfigPresets.validation.invalid'),
                fatal: true,
                cause: error,
            });
        }
    }

    /**
     * Load listing details from listings endpoint.
     *
     * Some content is specified in the API (e.g. images, map images). It is loaded from the listings endpoint and then
     * also attached to the "content" of a product, so it can be used in the offer flow (e.g. to show listing images or map).
     * The listing details are loaded based on the listingId defined in the product config.
     *
     * Listings are either loaded from a local source (local json files) or from the API, depending on the product config (isLocalListing),
     * to allow flexibility during development and fallback if the API is not yet ready. The request and response structure is the same for both sources.
     */
    async function applyListingContent() {
        const { product } = useProduct(route);

        if (product.value.content?.listingDetails || !product.value.config.listingId) {
            return;
        }
        if (!product.value.content) {
            product.value.content = {} as OfferFlowContent;
        }

        // Get listing content either from API or from local source, depending on product config
        if (product.value.config.isLocalListing) {
            product.value.content.listingDetails = (await apiLoadLocalListingDetails<ListingDetails>(product.value.config.listingId)).data;
        }
        else {
            product.value.content.listingDetails = (await apiLoadListingDetails<ListingDetails>(product.value.config.listingId)).data;
        }
    }

    return {
        applyCMSOfferFlowContent,
        applyListingContent,
    };
}
